import { Component, OnInit, OnDestroy } from '@angular/core';
import { NgxPermissionsService } from 'ngx-permissions';
import { AppComponent } from 'src/app/app.component';
import { AppMainComponent } from 'src/app/app.main.component';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { getUserDefaultProfile, getUserRoles } from 'src/app/core/functions';
import { Permission } from 'src/app/core/enums/permissions';
import { Profile } from 'src/app/core/enums/profile';
import { ProfileService } from 'src/app/shared/profile.service';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { getCompanyStorage } from 'src/app/core/functions-localStorage';
import { AppearanceService } from 'src/app/shared/appearance.service';
import {
    LayoutDashboard,
    LifeBuoy,
    Building,
    Building2,
    Users,
    Clock,
    Calendar,
    CalendarOff,
    CalendarClock,
    Inbox,
    Euro,
    Monitor,
    LayoutGrid,
    FileBarChart2,
    Settings,
    ShieldCheck,
    ClipboardList,
    ScrollText,
    Landmark,
    History,
    RefreshCw,
} from 'lucide-angular';

@Component({
    selector: 'app-menu',
    templateUrl: './app.menu.component.html',
    standalone: false
})
export class AppMenuComponent implements OnInit, OnDestroy {
  public model: any[] = [];
  isAuthenticated: boolean;
  profiles: any;
  profile: any;
  subsProfile: Subscription;
  subsAuth: Subscription;

  constructor(
    public app: AppComponent,
    public appMain: AppMainComponent,
    public afAuth: AngularFireAuth,
    public router: Router,
    private permissionsService: NgxPermissionsService,
    public profileService: ProfileService,
    private appearanceService: AppearanceService
  ) {}

  get logo(): string {
    // El sidebar es siempre oscuro (independiente del tema), asi que
    // siempre usa la variante del logo pensada para fondo oscuro.
    return `assets/layout/images/yobing-dark.png`;
  }

  ngOnDestroy(): void {
    this.subsProfile?.unsubscribe();
    this.subsAuth?.unsubscribe();
  }

  ngOnInit() {
    this.subsAuth = this.afAuth.idTokenResult.subscribe((user) => {
      if (user) {
        const permissions = getUserRoles(user.claims);
        this.permissionsService.loadPermissions(permissions);
        this.profile = getUserDefaultProfile(user.claims);
        this.subsProfile = this.profileService.getProfile().subscribe((x) => {
          this.profile = x;
          this.checkItemMenuProfile(this.profile, this.router.url);
        });
      }
    });

    this.model = [
      {
        label: 'Dashboard',
        icon: LayoutDashboard,
        routerLink: ['/dashboard'],
        roles: [
          Permission.Admin,
          Permission.CompanyManager,
          Permission.WorkplaceManager,
          Permission.Employee,
        ],
        profiles: [
          Profile.Admin,
          Profile.CompanyManager,
          Profile.WorkplaceManager,
          Profile.Employee,
        ],
        show: true,
      },
      {
        label: 'Guía del trabajador',
        icon: LifeBuoy,
        routerLink: ['/faq'],
        roles: [
          Permission.Admin,
          Permission.CompanyManager,
          Permission.WorkplaceManager,
          Permission.Employee,
        ],
        profiles: [
          Profile.Admin,
          Profile.CompanyManager,
          Profile.WorkplaceManager,
          Profile.Employee,
        ],
        required: ['admincompany'],
        show: true,
      },
      {
        label: 'Empresa',
        icon: Building,
        routerLink: ['/company'],
        roles: [Permission.Admin, Permission.CompanyManager],
        profiles: [Profile.Admin, Profile.CompanyManager],
        required: ['admincompany'],
        show: true,
      },
      {
        label: 'Centros de trabajo',
        icon: Building2,
        routerLink: ['/workplaces'],
        roles: [Permission.Admin, Permission.CompanyManager],
        profiles: [Profile.Admin, Profile.CompanyManager],
        show: true,
      },
      {
        label: 'Empleados',
        icon: Users,
        routerLink: ['/employees'],
        roles: [
          Permission.Admin,
          Permission.CompanyManager,
          Permission.WorkplaceManager,
        ],
        profiles: [
          Profile.Admin,
          Profile.CompanyManager,
          Profile.WorkplaceManager,
        ],
        show: true,
      },
      {
        label: 'Fichajes',
        icon: Clock,
        routerLink: ['/registers'],
        roles: [
          Permission.Admin,
          Permission.CompanyManager,
          Permission.WorkplaceManager,
        ],
        profiles: [
          Profile.Admin,
          Profile.CompanyManager,
          Profile.WorkplaceManager,
        ],
        show: true,
      },
      {
        label: 'Fichajes',
        icon: Clock,
        routerLink: ['/my-registers'],
        roles: [
          Permission.CompanyManager,
          Permission.Employee,
          Permission.WorkplaceManager,
        ],
        profiles: [Profile.Employee],
        show: true,
      },
      {
        label: 'Calendario',
        icon: Calendar,
        routerLink: ['/my-calendars'],
        roles: [
          Permission.Employee,
          Permission.CompanyManager,
          Permission.WorkplaceManager,
        ],
        profiles: [Profile.Employee],
        show: true,
      },
      {
        label: 'Solicitudes',
        icon: Inbox,
        routerLink: ['/my-requests'],
        roles: [
          Permission.CompanyManager,
          Permission.Employee,
          Permission.WorkplaceManager,
        ],
        profiles: [Profile.Employee],
        show: true,
      },
      {
        label: 'Solicitudes',
        icon: Inbox,
        routerLink: ['/requests'],
        roles: [
          Permission.Admin,
          Permission.CompanyManager,
          Permission.WorkplaceManager,
        ],
        profiles: [
          Profile.Admin,
          Profile.CompanyManager,
          Profile.WorkplaceManager,
        ],
        show: true,
      },
      {
        label: 'Mis nóminas',
        icon: Euro,
        routerLink: ['/payrolls/mine'],
        roles: [
          Permission.Admin,
          Permission.CompanyManager,
          Permission.WorkplaceManager,
          Permission.Employee,
        ],
        profiles: [Profile.Employee],
        show: true,
      },
      {
        label: 'Nóminas',
        icon: Euro,
        routerLink: ['/payrolls'],
        roles: [Permission.Admin, Permission.CompanyManager],
        profiles: [Profile.Admin, Profile.CompanyManager],
        show: true,
      },
      {
        label: 'Reserva puestos',
        icon: Monitor,
        routerLink: ['/desks/reservations'],
        roles: [
          Permission.Admin,
          Permission.CompanyManager,
          Permission.WorkplaceManager,
          Permission.Employee,
        ],
        profiles: [
          Profile.Admin,
          Profile.CompanyManager,
          Profile.WorkplaceManager,
          Profile.Employee,
        ],
        show: true,
      },
      {
        label: 'Editor de puestos',
        icon: LayoutGrid,
        routerLink: ['/desks/layout'],
        roles: [
          Permission.Admin,
          Permission.CompanyManager,
          Permission.WorkplaceManager,
        ],
        profiles: [
          Profile.Admin,
          Profile.CompanyManager,
          Profile.WorkplaceManager,
        ],
        show: true,
      },
      {
        label: 'Informes',
        icon: FileBarChart2,
        routerLink: ['/reports'],
        roles: [
          Permission.Admin,
          Permission.CompanyManager,
          Permission.WorkplaceManager,
        ],
        profiles: [
          Profile.Admin,
          Profile.CompanyManager,
          Profile.WorkplaceManager,
        ],
        show: true,
      },
      {
        roles: [Permission.Admin, Permission.CompanyManager],
        profiles: [Profile.Admin, Profile.CompanyManager],
        separator: true,
      },
      {
        label: 'Configuración',
        icon: Settings,
        roles: [Permission.Admin, Permission.CompanyManager],
        profiles: [Profile.Admin, Profile.CompanyManager],
        show: true,
        items: [
          {
            label: 'Calendarios',
            icon: Calendar,
            routerLink: ['/calendars'],
            roles: [Permission.Admin, Permission.CompanyManager],
            profiles: [Profile.Admin, Profile.CompanyManager],
            show: true,
          },
          {
            label: 'Jornadas',
            icon: CalendarClock,
            routerLink: ['/work-schedules'],
            roles: [Permission.Admin, Permission.CompanyManager],
            profiles: [Profile.Admin, Profile.CompanyManager],
            show: true,
          },
          {
            label: 'Ausencias',
            icon: CalendarOff,
            routerLink: ['/days-off'],
            roles: [Permission.Admin, Permission.CompanyManager],
            profiles: [Profile.Admin, Profile.CompanyManager],
            show: true,
          },
        ],
      },
      {
        roles: [Permission.Admin],
        profiles: [Profile.Admin],
        separator: true,
      },
      {
        label: 'Administración',
        icon: ShieldCheck,
        roles: [Permission.Admin],
        profiles: [Profile.Admin, Profile.CompanyManager],
        show: true,
        items: [
          {
            label: 'Eventos',
            icon: ClipboardList,
            routerLink: ['/registers-company'],
            roles: [Permission.Admin],
            profiles: [Profile.Admin, Profile.CompanyManager],
            show: true,
          },
          {
            label: 'Logs',
            icon: ScrollText,
            routerLink: ['/logs'],
            roles: [Permission.Admin],
            profiles: [Profile.Admin, Profile.CompanyManager],
            show: true,
          },
          {
            label: 'Tenants',
            icon: Landmark,
            routerLink: ['/tenants'],
            roles: [Permission.Admin],
            profiles: [Profile.Admin],
            show: true,
          },
          {
            label: 'Versiones',
            icon: History,
            routerLink: ['/versions'],
            roles: [Permission.Admin],
            profiles: [Profile.Admin],
            show: true,
          },
          {
            label: 'Backfill fichajes',
            icon: RefreshCw,
            routerLink: ['/admin/timeentries-backfill'],
            roles: [Permission.Admin],
            profiles: [Profile.Admin],
            show: true,
          },
        ],
      },
    ];
  }

  checkItemMenuProfile(profile: Profile, url: string) {
    const search = '/' + url.split('/')[1];
    const match = this.findMenuItemByUrl(this.model, search);
    if (!match || !match.profiles || !match.profiles.includes(profile)) {
      this.router.navigate(['/dashboard']);
    }
  }

  private findMenuItemByUrl(items: any[], search: string): any {
    for (const it of items) {
      if (it.routerLink && it.routerLink.includes(search)) return it;
      if (it.items) {
        const found = this.findMenuItemByUrl(it.items, search);
        if (found) return found;
      }
    }
    return null;
  }

  checkProfile(itemProfiles) {
    let profileOk = itemProfiles ? itemProfiles.includes(this.profile) : true;
    if (profileOk && this.profile == Profile.Admin && itemProfiles.length > 1)
      profileOk = getCompanyStorage() != null;

    return profileOk;
  }

  showDivider(index: number) {
    return (
      index == 0 || this.model[index - 1].section != this.model[index].section
    );
  }
}
